import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, ClipboardList, AlertTriangle, CheckCircle2, Clock, FlaskConical } from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import EmptyState from '@/components/ui/EmptyState';
import { format } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ResultRow {
  id: string;
  paramName: string;
  value: string | number | null;
  unit: string;
  minSpec: number | null;
  maxSpec: number | null;
  status: string;
  enteredBy: string;
}

interface Worksheet {
  id: string;
  worksheetCode: string;
  sampleCode?: string;
  product: string;
  analyst: string;
  status: string;
  labName?: string;
  specId?: string;
  notes?: string;
  createdAt: string;
  results: ResultRow[];
  parameters?: { id: string; name: string; unit: string; minValue: number | null; maxValue: number | null }[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_WORKSHEETS: Worksheet[] = [
  // ── Existing 3 records ──────────────────────────────────────────────────────
  {
    id: '1', worksheetCode: 'WS-2024-001', sampleCode: 'SMP-2024-001', product: 'Amoxicillin 500mg',
    analyst: 'Priya Sharma', status: 'open', labName: 'Forge QC Central — Mumbai', createdAt: '2024-03-15T09:00:00Z',
    results: [],
    parameters: [
      { id: 'p1', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'p2', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null },
    ],
  },
  {
    id: '2', worksheetCode: 'WS-2024-002', sampleCode: 'SMP-2024-002', product: 'Paracetamol 650mg',
    analyst: 'Anil Verma', status: 'in_progress', labName: 'Forge QC Central — Mumbai', createdAt: '2024-03-16T08:00:00Z',
    results: [
      { id: 'r1', paramName: 'Assay', value: 87, unit: '%', minSpec: 95, maxSpec: 105, status: 'oos', enteredBy: 'Anil Verma' },
      { id: 'r2', paramName: 'Hardness', value: 14, unit: 'kP', minSpec: 5, maxSpec: 20, status: 'pass', enteredBy: 'Anil Verma' },
    ],
    parameters: [
      { id: 'p3', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'p4', name: 'Hardness', unit: 'kP', minValue: 5, maxValue: 20 },
    ],
  },
  {
    id: '3', worksheetCode: 'WS-2024-003', sampleCode: 'SMP-2024-003', product: 'Metformin 500mg',
    analyst: 'Meena Pillai', status: 'completed', labName: 'Forge QC Central — Mumbai', createdAt: '2024-03-10T07:00:00Z',
    results: [
      { id: 'r3', paramName: 'Assay', value: 99.2, unit: '%', minSpec: 93, maxSpec: 107, status: 'pass', enteredBy: 'Meena Pillai' },
      { id: 'r4', paramName: 'Related Substances', value: 0.08, unit: '%', minSpec: null, maxSpec: 0.5, status: 'pass', enteredBy: 'Meena Pillai' },
    ],
    parameters: [],
  },

  // ── 2023 worksheets ─────────────────────────────────────────────────────────
  {
    id: '4', worksheetCode: 'WS-2023-001', sampleCode: 'SMP-000009', product: 'Ciprofloxacin 500mg',
    analyst: 'Deepak Rao', status: 'completed', labName: 'Forge QC Central — Mumbai', createdAt: '2023-04-06T08:00:00Z',
    results: [
      { id: 'r5', paramName: 'Assay', value: 101.4, unit: '%', minSpec: 95, maxSpec: 105, status: 'pass', enteredBy: 'Deepak Rao' },
      { id: 'r6', paramName: 'Dissolution', value: 82.0, unit: '%', minSpec: 75, maxSpec: null, status: 'pass', enteredBy: 'Deepak Rao' },
      { id: 'r7', paramName: 'Heavy Metals', value: 4.2, unit: 'ppm', minSpec: null, maxSpec: 10, status: 'pass', enteredBy: 'Deepak Rao' },
    ],
    parameters: [
      { id: 'pp1', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp2', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null },
      { id: 'pp3', name: 'Heavy Metals', unit: 'ppm', minValue: null, maxValue: 10 },
    ],
  },
  {
    id: '5', worksheetCode: 'WS-2023-002', sampleCode: 'SMP-000010', product: 'Omeprazole 20mg',
    analyst: 'Sonal Desai', status: 'completed', labName: 'Forge R&D Lab — Pune', createdAt: '2023-05-12T09:00:00Z',
    results: [
      { id: 'r8', paramName: 'Assay', value: 97.8, unit: '%', minSpec: 90, maxSpec: 110, status: 'pass', enteredBy: 'Sonal Desai' },
      { id: 'r9', paramName: 'Water Content', value: 3.1, unit: '%', minSpec: null, maxSpec: 5, status: 'pass', enteredBy: 'Sonal Desai' },
    ],
    parameters: [
      { id: 'pp4', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp5', name: 'Water Content', unit: '%', minValue: null, maxValue: 5 },
    ],
  },
  {
    id: '6', worksheetCode: 'WS-2023-003', sampleCode: 'SMP-000012', product: 'Doxycycline 100mg',
    analyst: 'Arjun Pillai', status: 'completed', labName: 'Forge API Lab — Hyderabad', createdAt: '2023-07-21T08:00:00Z',
    results: [
      { id: 'r10', paramName: 'Assay', value: 104.2, unit: '%', minSpec: 90, maxSpec: 110, status: 'pass', enteredBy: 'Arjun Pillai' },
      { id: 'r11', paramName: 'Dissolution', value: 68.0, unit: '%', minSpec: 75, maxSpec: null, status: 'oos', enteredBy: 'Arjun Pillai' },
    ],
    parameters: [
      { id: 'pp6', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp7', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null },
    ],
  },
  {
    id: '7', worksheetCode: 'WS-2023-004', sampleCode: 'SMP-000013', product: 'Pantoprazole 40mg',
    analyst: 'Priya Sharma', status: 'completed', labName: 'Forge QC Central — Mumbai', createdAt: '2023-08-13T07:30:00Z',
    results: [
      { id: 'r12', paramName: 'Assay', value: 100.3, unit: '%', minSpec: 95, maxSpec: 105, status: 'pass', enteredBy: 'Priya Sharma' },
      { id: 'r13', paramName: 'Hardness', value: 13.5, unit: 'kP', minSpec: 8, maxSpec: 22, status: 'pass', enteredBy: 'Priya Sharma' },
      { id: 'r14', paramName: 'Disintegration', value: 12.0, unit: 'min', minSpec: null, maxSpec: 30, status: 'pass', enteredBy: 'Priya Sharma' },
    ],
    parameters: [
      { id: 'pp8', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp9', name: 'Hardness', unit: 'kP', minValue: 8, maxValue: 22 },
      { id: 'pp10', name: 'Disintegration', unit: 'min', minValue: null, maxValue: 30 },
    ],
  },
  {
    id: '8', worksheetCode: 'WS-2023-005', sampleCode: 'SMP-API-023-001', product: 'Cefuroxime 500mg API',
    analyst: 'Meena Pillai', status: 'reviewed', labName: 'Forge API Lab — Hyderabad', createdAt: '2023-09-03T08:00:00Z',
    results: [
      { id: 'r15', paramName: 'Assay', value: 99.1, unit: '%', minSpec: 96, maxSpec: 102, status: 'pass', enteredBy: 'Meena Pillai' },
      { id: 'r16', paramName: 'Particle Size (D90)', value: 118.4, unit: 'µm', minSpec: null, maxSpec: 150, status: 'pass', enteredBy: 'Meena Pillai' },
      { id: 'r17', paramName: 'Water Content', value: 12.8, unit: '%', minSpec: 11.5, maxSpec: 14.5, status: 'pass', enteredBy: 'Meena Pillai' },
      { id: 'r18', paramName: 'Heavy Metals', value: 8.0, unit: 'ppm', minSpec: null, maxSpec: 20, status: 'pass', enteredBy: 'Meena Pillai' },
    ],
    parameters: [
      { id: 'pp11', name: 'Assay', unit: '%', minValue: 96, maxValue: 102 },
      { id: 'pp12', name: 'Particle Size (D90)', unit: 'µm', minValue: null, maxValue: 150 },
      { id: 'pp13', name: 'Water Content', unit: '%', minValue: 11.5, maxValue: 14.5 },
      { id: 'pp14', name: 'Heavy Metals', unit: 'ppm', minValue: null, maxValue: 20 },
    ],
  },

  // ── Additional 2024 worksheets ──────────────────────────────────────────────
  {
    id: '9', worksheetCode: 'WS-2024-004', sampleCode: 'SMP-000016', product: 'Metronidazole 400mg',
    analyst: 'Deepak Rao', status: 'completed', labName: 'Forge R&D Lab — Pune', createdAt: '2024-01-11T08:00:00Z',
    results: [
      { id: 'r19', paramName: 'Assay', value: 102.1, unit: '%', minSpec: 95, maxSpec: 105, status: 'pass', enteredBy: 'Deepak Rao' },
      { id: 'r20', paramName: 'Dissolution', value: 81.0, unit: '%', minSpec: 75, maxSpec: null, status: 'pass', enteredBy: 'Deepak Rao' },
    ],
    parameters: [
      { id: 'pp15', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp16', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null },
    ],
  },
  {
    id: '10', worksheetCode: 'WS-2024-005', sampleCode: 'SMP-000018', product: 'Ondansetron 4mg Injection',
    analyst: 'Sonal Desai', status: 'completed', labName: 'Forge Sterile Lab — Chennai', createdAt: '2024-03-06T08:00:00Z',
    results: [
      { id: 'r21', paramName: 'Assay', value: 98.7, unit: '%', minSpec: 90, maxSpec: 110, status: 'pass', enteredBy: 'Sonal Desai' },
      { id: 'r22', paramName: 'pH', value: 3.6, unit: '', minSpec: 3.3, maxSpec: 4.0, status: 'pass', enteredBy: 'Sonal Desai' },
      { id: 'r23', paramName: 'Particulate Matter', value: 3.0, unit: 'particles/mL', minSpec: null, maxSpec: 10, status: 'pass', enteredBy: 'Sonal Desai' },
    ],
    parameters: [
      { id: 'pp17', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp18', name: 'pH', unit: '', minValue: 3.3, maxValue: 4.0 },
      { id: 'pp19', name: 'Particulate Matter', unit: 'particles/mL', minValue: null, maxValue: 10 },
    ],
  },
  {
    id: '11', worksheetCode: 'WS-2024-006', sampleCode: 'SMP-000020', product: 'Amikacin 500mg Injection',
    analyst: 'Arjun Pillai', status: 'completed', labName: 'Forge Sterile Lab — Chennai', createdAt: '2024-05-20T07:00:00Z',
    results: [
      { id: 'r24', paramName: 'Assay', value: 95.2, unit: '%', minSpec: 90, maxSpec: 110, status: 'pass', enteredBy: 'Arjun Pillai' },
      { id: 'r25', paramName: 'pH', value: 5.2, unit: '', minSpec: 3.5, maxSpec: 5.5, status: 'pass', enteredBy: 'Arjun Pillai' },
    ],
    parameters: [
      { id: 'pp20', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp21', name: 'pH', unit: '', minValue: 3.5, maxValue: 5.5 },
    ],
  },
  {
    id: '12', worksheetCode: 'WS-2024-007', sampleCode: 'SMP-000022', product: 'Paracetamol 500mg Liquid',
    analyst: 'Priya Sharma', status: 'completed', labName: 'Forge QC Central — Mumbai', createdAt: '2024-07-23T09:00:00Z',
    results: [
      { id: 'r26', paramName: 'Assay', value: 88.4, unit: '%', minSpec: 90, maxSpec: 110, status: 'oos', enteredBy: 'Priya Sharma' },
      { id: 'r27', paramName: 'pH', value: 4.3, unit: '', minSpec: 3.8, maxSpec: 5.5, status: 'pass', enteredBy: 'Priya Sharma' },
    ],
    parameters: [
      { id: 'pp22', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp23', name: 'pH', unit: '', minValue: 3.8, maxValue: 5.5 },
    ],
  },
  {
    id: '13', worksheetCode: 'WS-2024-008', sampleCode: 'SMP-000023', product: 'Salbutamol Sulphate API',
    analyst: 'Anil Verma', status: 'reviewed', labName: 'Forge API Lab — Hyderabad', createdAt: '2024-08-07T09:00:00Z',
    results: [
      { id: 'r28', paramName: 'Assay', value: 98.5, unit: '%', minSpec: 96, maxSpec: 102, status: 'pass', enteredBy: 'Anil Verma' },
      { id: 'r29', paramName: 'Particle Size (D90)', value: 62.0, unit: 'µm', minSpec: null, maxSpec: 100, status: 'pass', enteredBy: 'Anil Verma' },
      { id: 'r30', paramName: 'Related Substances', value: 0.12, unit: '%', minSpec: null, maxSpec: 0.3, status: 'pass', enteredBy: 'Anil Verma' },
    ],
    parameters: [
      { id: 'pp24', name: 'Assay', unit: '%', minValue: 96, maxValue: 102 },
      { id: 'pp25', name: 'Particle Size (D90)', unit: 'µm', minValue: null, maxValue: 100 },
      { id: 'pp26', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.3 },
    ],
  },
  {
    id: '14', worksheetCode: 'WS-2024-009', sampleCode: 'SMP-000026', product: 'Ibuprofen 200mg Liquid',
    analyst: 'Meena Pillai', status: 'in_progress', labName: 'Forge R&D Lab — Pune', createdAt: '2024-11-15T09:00:00Z',
    results: [
      { id: 'r31', paramName: 'Assay', value: 103.2, unit: '%', minSpec: 95, maxSpec: 105, status: 'pass', enteredBy: 'Meena Pillai' },
    ],
    parameters: [
      { id: 'pp27', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp28', name: 'pH', unit: '', minValue: 3.0, maxValue: 5.0 },
      { id: 'pp29', name: 'Viscosity', unit: 'mPa·s', minValue: 20, maxValue: 80 },
    ],
  },

  // ── 2025 worksheets ─────────────────────────────────────────────────────────
  {
    id: '15', worksheetCode: 'WS-2025-001', sampleCode: 'SMP-000028', product: 'Paracetamol 650mg',
    analyst: 'Deepak Rao', status: 'completed', labName: 'Forge QC Central — Mumbai', createdAt: '2025-01-16T08:00:00Z',
    results: [
      { id: 'r32', paramName: 'Assay', value: 99.5, unit: '%', minSpec: 95, maxSpec: 105, status: 'pass', enteredBy: 'Deepak Rao' },
      { id: 'r33', paramName: 'Hardness', value: 11.8, unit: 'kP', minSpec: 5, maxSpec: 20, status: 'pass', enteredBy: 'Deepak Rao' },
      { id: 'r34', paramName: 'Disintegration', value: 8.0, unit: 'min', minSpec: null, maxSpec: 15, status: 'pass', enteredBy: 'Deepak Rao' },
    ],
    parameters: [
      { id: 'pp30', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp31', name: 'Hardness', unit: 'kP', minValue: 5, maxValue: 20 },
      { id: 'pp32', name: 'Disintegration', unit: 'min', minValue: null, maxValue: 15 },
    ],
  },
  {
    id: '16', worksheetCode: 'WS-2025-002', sampleCode: 'SMP-000030', product: 'Meropenem 1g Injection',
    analyst: 'Sonal Desai', status: 'in_progress', labName: 'Forge Sterile Lab — Chennai', createdAt: '2025-03-10T07:00:00Z',
    results: [
      { id: 'r35', paramName: 'Assay', value: 97.3, unit: '%', minSpec: 90, maxSpec: 110, status: 'pass', enteredBy: 'Sonal Desai' },
      { id: 'r36', paramName: 'Reconstituted pH', value: 7.9, unit: '', minSpec: 7.0, maxSpec: 8.5, status: 'pass', enteredBy: 'Sonal Desai' },
    ],
    parameters: [
      { id: 'pp33', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp34', name: 'Reconstituted pH', unit: '', minValue: 7.0, maxValue: 8.5 },
      { id: 'pp35', name: 'Water Content (KF)', unit: '%', minValue: null, maxValue: 2.5 },
    ],
  },
  {
    id: '17', worksheetCode: 'WS-2025-003', sampleCode: 'SMP-000031', product: 'Atorvastatin 20mg',
    analyst: 'Arjun Pillai', status: 'in_progress', labName: 'Forge API Lab — Hyderabad', createdAt: '2025-04-14T09:00:00Z',
    results: [
      { id: 'r37', paramName: 'Assay', value: 100.8, unit: '%', minSpec: 90, maxSpec: 110, status: 'pass', enteredBy: 'Arjun Pillai' },
    ],
    parameters: [
      { id: 'pp36', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp37', name: 'Dissolution', unit: '%', minValue: 80, maxValue: null },
      { id: 'pp38', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.3 },
    ],
  },
  {
    id: '18', worksheetCode: 'WS-2025-004', sampleCode: 'SMP-000033', product: 'Ciprofloxacin 250mg',
    analyst: 'Priya Sharma', status: 'completed', labName: 'Forge QC Central — Mumbai', createdAt: '2025-06-06T07:30:00Z',
    results: [
      { id: 'r38', paramName: 'Assay', value: 96.4, unit: '%', minSpec: 95, maxSpec: 105, status: 'pass', enteredBy: 'Priya Sharma' },
      { id: 'r39', paramName: 'Dissolution', value: 79.0, unit: '%', minSpec: 75, maxSpec: null, status: 'pass', enteredBy: 'Priya Sharma' },
    ],
    parameters: [
      { id: 'pp39', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp40', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null },
    ],
  },
  {
    id: '19', worksheetCode: 'WS-2025-005', sampleCode: 'SMP-000037', product: 'Azithromycin 500mg',
    analyst: 'Anil Verma', status: 'in_progress', labName: 'Forge R&D Lab — Pune', createdAt: '2025-10-19T09:00:00Z',
    results: [
      { id: 'r40', paramName: 'Assay', value: 103.5, unit: '%', minSpec: 93, maxSpec: 107, status: 'pass', enteredBy: 'Anil Verma' },
    ],
    parameters: [
      { id: 'pp41', name: 'Assay', unit: '%', minValue: 93, maxValue: 107 },
      { id: 'pp42', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null },
    ],
  },
  {
    id: '20', worksheetCode: 'WS-2025-006', sampleCode: 'SMP-000036', product: 'Ondansetron 8mg',
    analyst: 'Deepak Rao', status: 'open', labName: 'Forge QC Central — Mumbai', createdAt: '2025-10-01T08:00:00Z',
    results: [],
    parameters: [
      { id: 'pp43', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp44', name: 'Dissolution', unit: '%', minValue: 80, maxValue: null },
      { id: 'pp45', name: 'Hardness', unit: 'kP', minValue: 5, maxValue: 18 },
    ],
  },

  // ── 2026 worksheets ─────────────────────────────────────────────────────────
  {
    id: '21', worksheetCode: 'WS-2026-001', sampleCode: 'SMP-000001', product: 'Amoxicillin 500mg',
    analyst: 'Meena Pillai', status: 'in_progress', labName: 'Forge QC Central — Mumbai', createdAt: '2026-03-14T08:30:00Z',
    results: [
      { id: 'r41', paramName: 'Assay', value: 98.6, unit: '%', minSpec: 90, maxSpec: 110, status: 'pass', enteredBy: 'Meena Pillai' },
    ],
    parameters: [
      { id: 'pp46', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp47', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null },
      { id: 'pp48', name: 'Water Content', unit: '%', minValue: null, maxValue: 5 },
    ],
  },
  {
    id: '22', worksheetCode: 'WS-2026-002', sampleCode: 'SMP-000004', product: 'Ibuprofen 400mg',
    analyst: 'Ravi Iyer', status: 'in_progress', labName: 'Forge R&D Lab — Pune', createdAt: '2026-03-18T09:00:00Z',
    results: [
      { id: 'r42', paramName: 'Assay', value: 99.9, unit: '%', minSpec: 95, maxSpec: 105, status: 'pass', enteredBy: 'Ravi Iyer' },
      { id: 'r43', paramName: 'Hardness', value: 14.5, unit: 'kP', minSpec: 6, maxSpec: 18, status: 'pass', enteredBy: 'Ravi Iyer' },
    ],
    parameters: [
      { id: 'pp49', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp50', name: 'Dissolution', unit: '%', minValue: 70, maxValue: null },
      { id: 'pp51', name: 'Hardness', unit: 'kP', minValue: 6, maxValue: 18 },
      { id: 'pp52', name: 'Disintegration', unit: 'min', minValue: null, maxValue: 30 },
    ],
  },
  {
    id: '23', worksheetCode: 'WS-2026-003', sampleCode: 'SMP-000008', product: 'Atorvastatin 10mg',
    analyst: 'Priya Sharma', status: 'open', labName: 'Forge QC Central — Mumbai', createdAt: '2026-03-20T08:30:00Z',
    results: [],
    parameters: [
      { id: 'pp53', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp54', name: 'Dissolution', unit: '%', minValue: 80, maxValue: null },
      { id: 'pp55', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.3 },
    ],
  },
  {
    id: '24', worksheetCode: 'WS-2026-004', sampleCode: 'SMP-000039', product: 'Pantoprazole 40mg',
    analyst: 'Sonal Desai', status: 'open', labName: 'Forge QC Central — Mumbai', createdAt: '2026-03-28T10:30:00Z',
    results: [],
    parameters: [
      { id: 'pp56', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 },
      { id: 'pp57', name: 'Disintegration', unit: 'min', minValue: null, maxValue: 30 },
    ],
  },
  {
    id: '25', worksheetCode: 'WS-2026-005', sampleCode: 'SMP-000040', product: 'Cefixime 200mg',
    analyst: 'Arjun Pillai', status: 'open', labName: 'Forge Sterile Lab — Chennai', createdAt: '2026-04-03T08:00:00Z',
    results: [],
    parameters: [
      { id: 'pp58', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 },
      { id: 'pp59', name: 'pH', unit: '', minValue: 3.5, maxValue: 5.5 },
      { id: 'pp60', name: 'Viscosity', unit: 'mPa·s', minValue: 20, maxValue: 80 },
    ],
  },
];

const MOCK_SPECS = [
  { id: 's1', specCode: 'SPEC-001', product: 'Amoxicillin 500mg', parameters: [{ id: 'p1', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 }, { id: 'p2', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null }, { id: 'p3', name: 'Water Content', unit: '%', minValue: null, maxValue: 5 }] },
  { id: 's2', specCode: 'SPEC-002', product: 'Paracetamol 650mg', parameters: [{ id: 'p4', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 }, { id: 'p5', name: 'Hardness', unit: 'kP', minValue: 5, maxValue: 20 }] },
  { id: 's3', specCode: 'SPEC-003', product: 'Metformin 500mg', parameters: [{ id: 'p6', name: 'Assay', unit: '%', minValue: 93, maxValue: 107 }, { id: 'p7', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.5 }] },
  { id: 's4', specCode: 'SPEC-005', product: 'Ibuprofen 400mg', parameters: [{ id: 'p8', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 }, { id: 'p9', name: 'Dissolution', unit: '%', minValue: 70, maxValue: null }, { id: 'p10', name: 'Hardness', unit: 'kP', minValue: 6, maxValue: 18 }, { id: 'p11', name: 'Disintegration', unit: 'min', minValue: null, maxValue: 30 }] },
  { id: 's5', specCode: 'SPEC-006', product: 'Atorvastatin 10mg', parameters: [{ id: 'p12', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 }, { id: 'p13', name: 'Dissolution', unit: '%', minValue: 80, maxValue: null }, { id: 'p14', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.3 }] },
  { id: 's6', specCode: 'SPEC-007', product: 'Ciprofloxacin 500mg', parameters: [{ id: 'p15', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 }, { id: 'p16', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null }, { id: 'p17', name: 'Heavy Metals', unit: 'ppm', minValue: null, maxValue: 10 }] },
  { id: 's7', specCode: 'SPEC-008', product: 'Ondansetron 4mg Injection', parameters: [{ id: 'p18', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 }, { id: 'p19', name: 'pH', unit: '', minValue: 3.3, maxValue: 4.0 }, { id: 'p20', name: 'Particulate Matter', unit: 'particles/mL', minValue: null, maxValue: 10 }] },
  { id: 's8', specCode: 'SPEC-009', product: 'Amoxicillin 500mg API', parameters: [{ id: 'p21', name: 'Assay', unit: '%', minValue: 96, maxValue: 102 }, { id: 'p22', name: 'Particle Size (D90)', unit: 'µm', minValue: null, maxValue: 150 }, { id: 'p23', name: 'Water Content', unit: '%', minValue: 11.5, maxValue: 14.5 }] },
  { id: 's9', specCode: 'SPEC-013', product: 'Levofloxacin 500mg', parameters: [{ id: 'p24', name: 'Assay', unit: '%', minValue: 95, maxValue: 105 }, { id: 'p25', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null }, { id: 'p26', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.5 }] },
  { id: 's10', specCode: 'SPEC-014', product: 'Meropenem 1g Injection', parameters: [{ id: 'p27', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 }, { id: 'p28', name: 'Reconstituted pH', unit: '', minValue: 7.0, maxValue: 8.5 }, { id: 'p29', name: 'Water Content (KF)', unit: '%', minValue: null, maxValue: 2.5 }] },
  { id: 's11', specCode: 'SPEC-015', product: 'Doxycycline 100mg', parameters: [{ id: 'p30', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 }, { id: 'p31', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null }, { id: 'p32', name: 'Water Content', unit: '%', minValue: null, maxValue: 6 }] },
  { id: 's12', specCode: 'SPEC-012', product: 'Paracetamol 500mg Liquid', parameters: [{ id: 'p33', name: 'Assay', unit: '%', minValue: 90, maxValue: 110 }, { id: 'p34', name: 'pH', unit: '', minValue: 3.8, maxValue: 5.5 }, { id: 'p35', name: 'Viscosity', unit: 'mPa·s', minValue: 20, maxValue: 80 }] },
];

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color, highlight }: { label: string; value: number; icon: React.ElementType; color: string; highlight?: boolean }) {
  return (
    <div className={`card p-4 flex items-center gap-4 ${highlight && value > 0 ? 'border border-red-200 bg-red-50/30' : ''}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className={`text-2xl font-bold font-serif ${highlight && value > 0 ? 'text-red-600' : 'text-text'}`}>{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TestWorksheet() {
  const [activeTab, setActiveTab] = useState<'worksheets' | 'results'>('worksheets');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [labFilter, setLabFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedWS, setSelectedWS] = useState<Worksheet | null>(null);
  const [showEnterResults, setShowEnterResults] = useState(false);
  const [resultValues, setResultValues] = useState<Record<string, string>>({});

  const [createForm, setCreateForm] = useState({
    labId: '', sampleCode: '', specId: '', analyst: '', notes: '',
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['lims-worksheets', { search, status: statusFilter, lab: labFilter, page }],
    queryFn: () =>
      api.get('/lims/worksheets', { params: { search, status: statusFilter, lab: labFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_WORKSHEETS, meta: { total: MOCK_WORKSHEETS.length } })),
  });

  const { data: specsData } = useQuery({
    queryKey: ['lims-specs-all'],
    queryFn: () =>
      api.get('/lims/specs', { params: { status: 'active', limit: 100 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_SPECS })),
  });

  const { data: labsData } = useQuery({
    queryKey: ['labs-list'],
    queryFn: () => api.get('/labs', { params: { limit: 50 } }).then((r) => r.data).catch(() => ({ data: [] })),
  });
  const allLabs: { id: string; name: string }[] = Array.isArray(labsData?.data) ? labsData.data : [];

  const rows: Worksheet[] = (Array.isArray(data?.data) && data.data.length > 0) ? data.data : MOCK_WORKSHEETS;
  const total: number = data?.meta?.total ?? rows.length;
  const allSpecs = Array.isArray(specsData?.data) ? specsData.data : MOCK_SPECS;

  const stats = {
    open: rows.filter((w) => w.status === 'open').length,
    inProgress: rows.filter((w) => w.status === 'in_progress').length,
    completed: rows.filter((w) => w.status === 'completed').length,
    oos: rows.reduce((acc, w) => acc + (Array.isArray(w.results) ? w.results.filter((r) => r.status === 'oos').length : 0), 0),
  };

  const createMutation = useMutation({
    mutationFn: (payload: typeof createForm) => api.post('/lims/worksheets', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-worksheets'] });
      setShowCreate(false);
      setCreateForm({ labId: '', sampleCode: '', specId: '', analyst: '', notes: '' });
    },
  });

  const submitResultsMutation = useMutation({
    mutationFn: ({ wsId, results }: { wsId: string; results: { paramId: string; value: string }[] }) =>
      api.post(`/lims/worksheets/${wsId}/results`, { results }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-worksheets'] });
      setShowEnterResults(false);
      setResultValues({});
    },
  });

  const isOOS = (value: string, min: number | null, max: number | null): boolean => {
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    if (min !== null && num < min) return true;
    if (max !== null && num > max) return true;
    return false;
  };

  const selectedSpecParams = selectedWS?.parameters || [];

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'worksheetCode', label: 'Worksheet', width: 'w-36',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.worksheetCode)}</span>,
    },
    {
      key: 'product', label: 'Product / Sample',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.product)}</p>
          <p className="text-xs text-text-dim">{row.sampleCode ? String(row.sampleCode) : '—'}</p>
        </div>
      ),
    },
    {
      key: 'analyst', label: 'Analyst',
      render: (row) => <span className="text-xs text-text">{String(row.analyst)}</span>,
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
    {
      key: 'labName', label: 'Lab',
      render: (row) => <span className="text-xs text-text-muted">{row.labName ? String(row.labName) : '—'}</span>,
    },
    {
      key: 'createdAt', label: 'Created',
      render: (row) => (
        <span className="text-xs font-mono text-text-dim">
          {row.createdAt ? format(new Date(String(row.createdAt)), 'd MMM yyyy') : '—'}
        </span>
      ),
    },
  ];

  const resultColumns: Column<Record<string, unknown>>[] = [
    {
      key: 'worksheetCode', label: 'Worksheet', width: 'w-36',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.worksheetCode)}</span>,
    },
    {
      key: 'product', label: 'Product',
      render: (row) => <span className="text-sm font-medium text-text">{String(row.product)}</span>,
    },
    {
      key: 'results', label: 'Results Summary',
      render: (row) => {
        const results = row.results as ResultRow[];
        if (!Array.isArray(results) || results.length === 0) {
          return <span className="text-xs text-text-dim">No results entered</span>;
        }
        const oosCount = results.filter((r) => r.status === 'oos').length;
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs text-text">{results.length} results</span>
            {oosCount > 0 && (
              <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                {oosCount} OOS
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Test Worksheets"
        subtitle="Results entry and QC worksheet management"
        actions={
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={13} /> Create Worksheet
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Open" value={stats.open} icon={Clock} color="#2563EB" />
        <StatCard label="In Progress" value={stats.inProgress} icon={FlaskConical} color="#b07d1a" />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} color="#16A34A" />
        <StatCard label="OOS Results" value={stats.oos} icon={AlertTriangle} color="#DC2626" highlight />
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 p-1 bg-bg rounded-xl w-fit">
        {(['worksheets', 'results'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all capitalize ${
              activeTab === tab
                ? 'bg-card shadow-sm text-text'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {tab === 'worksheets' ? 'Worksheets' : 'Results'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by worksheet code, product..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="select w-36"
        >
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <select
          value={labFilter}
          onChange={(e) => { setLabFilter(e.target.value); setPage(1); }}
          className="select w-48"
        >
          <option value="">All Labs</option>
          {allLabs.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      </div>

      <DataTable
        columns={activeTab === 'worksheets' ? columns : resultColumns}
        data={rows as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedWS(row as unknown as Worksheet)}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Worksheet Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selectedWS}
        onClose={() => setSelectedWS(null)}
        title={selectedWS?.worksheetCode || ''}
        subtitle={`${selectedWS?.product} · ${selectedWS?.analyst}`}
        width={600}
        footer={
          selectedWS && selectedWS.status !== 'completed' ? (
            <button onClick={() => setShowEnterResults(true)} className="btn-primary">
              <ClipboardList size={13} /> Enter Results
            </button>
          ) : undefined
        }
      >
        {selectedWS && (
          <div className="space-y-5">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Status', value: <PillBadge value={selectedWS.status} showDot /> },
                { label: 'Analyst', value: selectedWS.analyst },
                { label: 'Sample Code', value: selectedWS.sampleCode || '—' },
                { label: 'Lab', value: selectedWS.labName || '—' },
                { label: 'Created', value: format(new Date(selectedWS.createdAt), 'd MMM yyyy') },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 bg-bg rounded-lg">
                  <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">{label}</p>
                  <div className="text-xs font-medium text-text">{typeof value === 'string' ? value : value}</div>
                </div>
              ))}
            </div>

            {selectedWS.notes && (
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Notes</p>
                <p className="text-sm text-text">{selectedWS.notes}</p>
              </div>
            )}

            {/* Results Table */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
                <FlaskConical size={12} className="text-teal" /> Test Results ({Array.isArray(selectedWS.results) ? selectedWS.results.length : 0})
              </p>
              {!Array.isArray(selectedWS.results) || selectedWS.results.length === 0 ? (
                <EmptyState icon="data" title="No results yet" description="Click Enter Results to submit test data." />
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-bg border-b border-border">
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Parameter</th>
                        <th className="text-right px-3 py-2 text-text-dim font-semibold">Result</th>
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Unit</th>
                        <th className="text-center px-3 py-2 text-text-dim font-semibold">Spec (Min–Max)</th>
                        <th className="text-center px-3 py-2 text-text-dim font-semibold">Status</th>
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedWS.results.map((r, idx) => (
                        <tr key={r.id} className={`${idx % 2 === 0 ? 'bg-card' : 'bg-bg'} ${r.status === 'oos' ? 'bg-red-50/50' : ''}`}>
                          <td className="px-3 py-2 font-medium text-text">{r.paramName}</td>
                          <td className={`px-3 py-2 text-right font-mono font-semibold ${r.status === 'oos' ? 'text-red-600' : 'text-text'}`}>
                            {r.value !== null && r.value !== undefined ? (Number(r.value) || 0).toFixed(2) : '—'}
                          </td>
                          <td className="px-3 py-2 text-text-muted">{r.unit}</td>
                          <td className="px-3 py-2 text-center text-text-muted">
                            {r.minSpec !== null ? (Number(r.minSpec) || 0).toFixed(1) : 'NLT —'} – {r.maxSpec !== null ? (Number(r.maxSpec) || 0).toFixed(1) : '— NMT'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <PillBadge value={r.status === 'oos' ? 'rejected' : 'approved'} label={r.status.toUpperCase()} />
                          </td>
                          <td className="px-3 py-2 text-text-dim">{r.enteredBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </SideDrawer>

      {/* Create Worksheet Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Worksheet"
        subtitle="Open a new test worksheet for analysis"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() => createMutation.mutate(createForm)}
              disabled={createMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Worksheet'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Lab</label>
            <select
              className="select"
              value={createForm.labId}
              onChange={(e) => setCreateForm((f) => ({ ...f, labId: e.target.value }))}
            >
              <option value="">Select lab...</option>
              {allLabs.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Sample Code</label>
            <input
              className="input"
              value={createForm.sampleCode}
              onChange={(e) => setCreateForm((f) => ({ ...f, sampleCode: e.target.value }))}
              placeholder="Search or enter sample code..."
            />
          </div>
          <div>
            <label className="label">Specification</label>
            <select
              className="select"
              value={createForm.specId}
              onChange={(e) => setCreateForm((f) => ({ ...f, specId: e.target.value }))}
            >
              <option value="">Select specification...</option>
              {allSpecs.map((s: { id: string; specCode: string; product: string }) => (
                <option key={s.id} value={s.id}>{s.specCode} — {s.product}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Analyst</label>
            <input
              className="input"
              value={createForm.analyst}
              onChange={(e) => setCreateForm((f) => ({ ...f, analyst: e.target.value }))}
              placeholder="Analyst name..."
            />
          </div>
          <div className="col-span-2">
            <label className="label">Notes</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={createForm.notes}
              onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Additional notes..."
            />
          </div>
        </div>
      </Modal>

      {/* Enter Results Modal */}
      <Modal
        isOpen={showEnterResults}
        onClose={() => { setShowEnterResults(false); setResultValues({}); }}
        title="Enter Results"
        subtitle={selectedWS ? `Worksheet: ${selectedWS.worksheetCode}` : ''}
        size="lg"
        footer={
          <>
            <button onClick={() => { setShowEnterResults(false); setResultValues({}); }} className="btn-secondary">Cancel</button>
            <button
              onClick={() => {
                if (!selectedWS) return;
                const results = selectedSpecParams.map((p) => ({
                  paramId: p.id,
                  value: resultValues[p.id] || '',
                }));
                submitResultsMutation.mutate({ wsId: selectedWS.id, results });
              }}
              disabled={submitResultsMutation.isPending}
              className="btn-primary"
            >
              {submitResultsMutation.isPending ? 'Saving...' : 'Save Results'}
            </button>
          </>
        }
      >
        {selectedSpecParams.length === 0 ? (
          <EmptyState icon="data" title="No parameters" description="This worksheet has no spec parameters defined." />
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-text-muted">Enter measured values for each parameter. Values outside spec limits will be flagged as OOS.</p>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-bg border-b border-border">
                    <th className="text-left px-3 py-2.5 text-xs text-text-dim font-semibold">Parameter</th>
                    <th className="text-left px-3 py-2.5 text-xs text-text-dim font-semibold">Unit</th>
                    <th className="text-center px-3 py-2.5 text-xs text-text-dim font-semibold">Spec (Min – Max)</th>
                    <th className="text-left px-3 py-2.5 text-xs text-text-dim font-semibold">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSpecParams.map((p, idx) => {
                    const val = resultValues[p.id] || '';
                    const oos = val !== '' && isOOS(val, p.minValue, p.maxValue);
                    return (
                      <tr key={p.id} className={idx % 2 === 0 ? 'bg-card' : 'bg-bg'}>
                        <td className="px-3 py-2 font-medium text-text">{p.name}</td>
                        <td className="px-3 py-2 text-text-muted text-xs">{p.unit}</td>
                        <td className="px-3 py-2 text-center text-xs text-text-muted font-mono">
                          {p.minValue !== null ? (Number(p.minValue) || 0).toFixed(1) : '—'} – {p.maxValue !== null ? (Number(p.maxValue) || 0).toFixed(1) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <div className="relative">
                            <input
                              type="number"
                              step="any"
                              value={val}
                              onChange={(e) => setResultValues((rv) => ({ ...rv, [p.id]: e.target.value }))}
                              className={`input w-32 text-right font-mono text-sm ${oos ? 'border-red-400 bg-red-50 focus:ring-red-300 text-red-700' : ''}`}
                              placeholder="—"
                            />
                            {oos && (
                              <span className="absolute -top-2 -right-2 text-[10px] font-bold px-1 py-px rounded bg-red-600 text-white leading-tight">OOS</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
